import { customElement, property } from 'lit/decorators.js';
import { CSSResult, html, LitElement, nothing, TemplateResult, unsafeCSS } from 'lit';
import { consume } from '@lit/context';
import vtoIcon from '@breuni-ui/bdk/design-tokens/images/icons/o-ic-24_virtual-tryon.svg?element';
import styles from './vto-curated-teaser.scss?inline';
import { sharedCss } from '../../../scripts/style-bundle/shared.js';
import { i18nContext } from '../../../scripts/context/i18nContext.js';
import { Messages } from '../../../@types/wishlist.js';
import { hideIconFromAssistiveTechnology } from '../icon/icon-accessibility.js';

const BANNER_URL =
  'https://teaser.prod.breuni.de/breuninger/image/upload/v1784560798/ents/wishlist/premier-concierge/VTO_kuratierte-Listen.jpg';

@customElement('ents-vto-curated-teaser')
export class VtoCuratedTeaser extends LitElement {
  @property({ type: String }) variant: 'ad' | 'photo' = 'ad';

  @property({ type: Boolean }) dismissible = false;

  @property({ type: String, attribute: 'photo-url' }) photoUrl?: string;

  @consume({ context: i18nContext })
  private messages!: Messages;

  static get styles(): CSSResult[] {
    return [sharedCss, unsafeCSS(styles)];
  }

  protected render(): TemplateResult {
    return this.variant === 'photo' ? this.renderPhoto() : this.renderAd();
  }

  private renderAd() {
    return html`
      <section class="ents-vto-teaser ents-vto-teaser--ad" data-test-ents-vto-teaser="ad">
        <img class="ents-vto-teaser__banner" src="${BANNER_URL}" alt="" />
        <div class="ents-vto-teaser__overlay">
          <button
            class="bdk-button ents-vto-teaser__cta"
            type="button"
            variant="primary"
            inverted
            size="small"
            @click="${this.emitCta}"
            data-test-ents-vto-teaser-cta
          >
            ${hideIconFromAssistiveTechnology(vtoIcon())}
            <span>${this.messages['ents_wishlist_vto_teaser_cta']}</span>
          </button>
          ${this.dismissible
            ? html`<button
                class="bdk-button-ghost"
                inverted
                type="button"
                @click="${this.emitDismiss}"
                data-test-ents-vto-teaser-dismiss
              >
                <span class="bdk-strong-s">${this.messages['ents_wishlist_vto_teaser_dismiss']}</span>
              </button>`
            : nothing}
        </div>
      </section>
    `;
  }

  private renderPhoto() {
    return html`
      <section class="ents-vto-teaser ents-vto-teaser--photo" data-test-ents-vto-teaser="photo">
        <h2 class="ents-heading">${this.messages['ents_wishlist_vto_teaser_photo_title']}</h2>
        <div class="ents-vto-teaser__photo-wrap" @click="${this.emitCta}">
          <img class="ents-vto-teaser__photo" src="${this.photoUrl ?? ''}" alt="" data-test-ents-vto-teaser-photo />
          <button
            class="bdk-button-icon ents-vto-teaser__photo-cta"
            type="button"
            variant="primary"
            inverted
            size="extra-small"
            @click="${this.emitCta}"
            aria-label="${this.messages['ents_wishlist_vto_teaser_photo_change']}"
            data-test-ents-vto-teaser-cta
          >
            ${hideIconFromAssistiveTechnology(vtoIcon())}
          </button>
        </div>
      </section>
    `;
  }

  private emitCta = (e?: Event) => {
    // In the photo variant the CTA button sits inside a click-handling wrapper; stop so it fires once.
    e?.stopPropagation();
    this.dispatchEvent(new Event('ents:vto-teaser:cta', { bubbles: true, composed: true }));
  };

  private emitDismiss = () => {
    this.dispatchEvent(new Event('ents:vto-teaser:dismiss', { bubbles: true, composed: true }));
  };
}
